import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, Navigate, useNavigate, useLocation, matchPath } from 'react-router-dom';
import axios from 'axios';
import Home from './components/Home';
import Login from './components/Login';
import Profile from './components/Profile';
import Chat from './components/Chat';
import Register from './components/Register';
import './App.css';

import OppositionPage from './components/OppositionPage';

import Syllabus from './components/Syllabus';
import ExamByTopic from './components/ExamByTopic';
import ExamByLaw from './components/ExamByLaw';

import ExamPage from './components/ExamPage';

import ArticlePage from './components/ArticlePage';
import AdminPage from './components/AdminPage';
import OfficialExams from './components/OfficialExams';
import Insights from './components/Insights';
import GraphView from './components/GraphView';
import OpenQuestionsByTopic from './components/OpenQuestionsByTopic';
import OpenQuestionPractice from './components/OpenQuestionPractice';
import GeneratedExams from './components/GeneratedExams';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import TermsConditions from './components/legal/TermsConditions';
import CookiePolicy from './components/legal/CookiePolicy';
import AvisoLegal from './components/legal/AvisoLegal';
import CookieBanner from './components/CookieBanner';
import { rememberOpposition } from './utils/lastOpposition';

// Configure axios defaults
// En local (npm start) usa .env → http://localhost:8000
// En producción (npm run build) usa .env.production → https://api.almaley.com
axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
axios.defaults.withCredentials = true;

/**
 * Tras iniciar sesión (Google o email) se aterriza en la raíz con ?post_login=1
 * y el salto al perfil se hace aquí, en cliente. Así la autenticación no depende
 * de que el servidor estático sepa resolver rutas profundas.
 *
 * Tiene que vivir dentro de <Router>: los hooks de react-router necesitan ese
 * contexto y App queda fuera, porque es quien renderiza el Router.
 */
const PostLoginRedirect = ({ user }) => {
  const navigate = useNavigate();
  useEffect(() => {
    if (user && new URLSearchParams(window.location.search).get('post_login') === '1') {
      // replace: true quita el parámetro de la barra y evita volver atrás
      // al estado intermedio.
      navigate('/profile', { replace: true });
    }
  }, [user, navigate]);
  return null;
};

/**
 * Recuerda la oposición a la que entra el usuario (/oposicion/:oppositionName/...)
 * para que las páginas sin oposición en la ruta, como la de un artículo,
 * muestren las estadísticas de la oposición de la que se viene.
 */
const OppositionTracker = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const match = matchPath({ path: '/oposicion/:oppositionName', end: false }, pathname);
    if (match) rememberOpposition(match.params.oppositionName);
  }, [pathname]);
  return null;
};

const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkUser = async () => {
      try {
        // The HttpOnly cookie is sent automatically by the browser
        const { data } = await axios.get('/users/me', { timeout: 12000 });
        setUser(data);
      } catch (err) {
        console.log('User not logged in');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkUser();
  }, []);

  const PrivateRoute = ({ children }) => {
    if (loading) {
      return <div>Cargando...</div>; // Or a spinner component
    }
    return user ? children : <Navigate to="/login" />;
  };

  const handleLogout = async () => {
    try {
      await axios.post('/auth/logout');
    } catch (err) {
      console.warn('Logout endpoint failed', err);
    }
    setUser(null);
    // A la raíz, no a /login: es una ruta que el servidor estático siempre
    // resuelve. Sin sesión, PrivateRoute redirige a /login en cliente. La
    // recarga completa además limpia todo el estado de React, que es lo
    // deseable al cerrar sesión.
    window.location.href = '/';
  };

  return (
    <Router>
      <header className="header">
        <div className="header-inner">
          <Link to="/" className="logo">
            <img src="/logo-almaley.png" alt="Almaley Logo" className="logo-img" />
          </Link>
          <nav className="nav-menu">
            <ul>
              <li><Link to="/">Inicio</Link></li>
              <li><Link to="/chat">Chat</Link></li>
              <li><Link to="/profile">Perfil</Link></li>
              {user && user.is_admin && (
                <li><Link to="/admin">Admin</Link></li>
              )}
            </ul>
          </nav>
          <div className="nav-end">
            {user && (
              <button onClick={handleLogout} className="logout-btn">
                Cerrar sesión
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="main-content">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/" element={<PrivateRoute><Home user={user} /></PrivateRoute>} />
            <Route path="/chat" element={<PrivateRoute><Chat /></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><Profile user={user} onUserUpdated={setUser} /></PrivateRoute>} />
            <Route path="/oposicion/:oppositionName" element={<PrivateRoute><OppositionPage /></PrivateRoute>} />
            <Route path="/oposicion/:oppositionName/temario" element={<PrivateRoute><Syllabus /></PrivateRoute>} />
            <Route path="/oposicion/:oppositionName/examen-tema" element={<PrivateRoute><ExamByTopic /></PrivateRoute>} />
            <Route path="/oposicion/:oppositionName/examen-ley" element={<PrivateRoute><ExamByLaw /></PrivateRoute>} />
            <Route path="/oposicion/:oppositionName/examenes-oficiales" element={<PrivateRoute><OfficialExams /></PrivateRoute>} />
            <Route path="/oposicion/:oppositionName/examenes-generados" element={<PrivateRoute><GeneratedExams /></PrivateRoute>} />
            <Route path="/oposicion/:oppositionName/insights" element={<PrivateRoute><Insights /></PrivateRoute>} />
            <Route path="/oposicion/:oppositionName/grafo" element={<PrivateRoute><GraphView /></PrivateRoute>} />
            <Route path="/oposicion/:oppositionName/preguntas-abiertas" element={<PrivateRoute><OpenQuestionsByTopic /></PrivateRoute>} />
            <Route path="/oposicion/:oppositionName/preguntas-abiertas/:lessonName" element={<PrivateRoute><OpenQuestionPractice /></PrivateRoute>} />
            <Route path="/exam/:exam_type/:exam_name" element={<PrivateRoute><ExamPage /></PrivateRoute>} />
            <Route path="/article/:law_name/:article_number" element={<PrivateRoute><ArticlePage /></PrivateRoute>} />
            <Route path="/admin" element={<PrivateRoute><AdminPage user={user} /></PrivateRoute>} />
            <Route path="/legal/aviso-legal" element={<AvisoLegal />} />
            <Route path="/legal/privacidad" element={<PrivacyPolicy />} />
            <Route path="/legal/terminos" element={<TermsConditions />} />
            <Route path="/legal/cookies" element={<CookiePolicy />} />
          </Routes>
      </main>

      <footer className="app-footer">
        <div className="app-footer-inner">
          <span className="app-footer-brand">© {new Date().getFullYear()} Almaley</span>
          <nav className="app-footer-links">
            <Link to="/legal/aviso-legal">Aviso legal</Link>
            <Link to="/legal/privacidad">Privacidad</Link>
            <Link to="/legal/terminos">Términos</Link>
            <Link to="/legal/cookies">Cookies</Link>
            <Link to="/legal/cookies">Gestionar cookies</Link>
          </nav>
        </div>
      </footer>

      <CookieBanner />
      <PostLoginRedirect user={user} />
      <OppositionTracker />
    </Router>
  );
};

export default App;